import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useLanguage from 'utils/hooks/useLanguage';
import { LANGUAGES } from 'utils/Constants';
import { useDispatch, useSelector } from 'react-redux';
import { formatDateShort } from 'utils/DateUtils';

import styles from '../WriterClient/Homepage.module.css';
import { WebsiteFilter } from 'components/shared/WebsiteFilter';
import axiosPrivate from 'config/axiosPrivate';
import { adminClientClientListSelector } from 'store/AdminClient/ClientList';
import { fetchAdminClientClientList } from 'store/AdminClient/ClientList/features';
import { setCurrentRedaction } from 'store/AdminClient/RedactionData';
import { updateOrderDetails, deleteAllTexts } from 'store/AdminClient/OrderDisplay';
import usePageTracking from 'utils/hooks/usePageTracking';

const PROJECT_MANAGER_DASHBOARD_PAGE = '/ProjectManager/MainMenu';
const STABLE_EMPTY_WEBSITES = [];

export default function ProjectManagerHomePage() {
  usePageTracking(PROJECT_MANAGER_DASHBOARD_PAGE);

  const { t } = useTranslation();
  const { language, changeLanguage } = useLanguage();
  const dispatch = useDispatch();

  const getTextStatusLabel = (content) => {
    if (content?.deployed) return t('En ligne');
    if (content?.validated) return t('Validé');
    if (content?.delivered) return t('Livré');
    return t('Brouillon');
  };

  const getTextStatusIcon = (content) => {
    if (content?.deployed) return '/Images/deployed-text-icon.png';
    if (content?.validated) return '/Images/validate-text-icon.png';
    if (content?.delivered) return '/Images/delivred-text-icon.png';
    return '/Images/drafted-text-icon.png';
  };

  const getProjectStatusLabel = (project) => {
    const status = project?.status;
    if (!status) return '-';
    const normalizedStatus = String(status).toUpperCase().trim();
    switch (normalizedStatus) {
      case 'IN_PROGRESS':
        return t('En cours');
      case 'DELAYED':
        return t('En retard');
      case 'VALIDATED':
        return t('Validé');
      case 'NON_ATTRIBUTED':
        return t('Non attribué');
      default:
        return status;
    }
  };

  const getProjectStatusIcon = (project) => {
    const status = project?.status;
    if (!status) return '/Images/drafted-text-icon.png';
    const normalizedStatus = String(status).toUpperCase().trim();
    switch (normalizedStatus) {
      case 'IN_PROGRESS':
        return '/Images/delivred-text-icon.png';
      case 'DELAYED':
        return '/Images/drafted-text-icon.png';
      case 'VALIDATED':
        return '/Images/validate-text-icon.png';
      case 'NON_ATTRIBUTED':
        return '/Images/drafted-text-icon.png';
      default:
        return '/Images/drafted-text-icon.png';
    }
  };

  const [urlSearch, setUrlSearch] = useState('');
  const history = useHistory();
  const [latestContents, setLatestContents] = useState([]);
  const [isLoadingContents, setIsLoadingContents] = useState(false);
  const [latestProjects, setLatestProjects] = useState([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [projectStats, setProjectStats] = useState({
    numberOfProjectInProgress: 0,
    numberOfProjectInDelay: 0,
    numberOfProjectValidated: 0,
    numberOfProjects: 0,
  });
  const [contentStats, setContentStats] = useState({
    numberOfTextInDraft: 0,
    numberOfTextDelivered: 0,
    numberOfTextDeployed: 0,
    numberOfTextValidated: 0,
    numberOfTextValidatedAndNotDeployed: 0,
    averageNumberOfTextDeployedPerMonth: 0,
    averageNumberOfTextDeployedPerYear: 0,
    averageNumberOfTextDeliveredPerMonth: 0,
    averageNumberOfTextDeliveredPerYear: 0,
    averageNumberOfProjectWhereAllTextDeliveredPerYear: 0,
    totalNumberOfText: 0,
  });
  const [isLoadingContentStats, setIsLoadingContentStats] = useState(false);
  const [monthlyTextsStats, setMonthlyTextsStats] = useState({ delivered: {}, deployed: {} });
  const [isLoadingMonthlyStats, setIsLoadingMonthlyStats] = useState(false);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const yearDropdownRef = useRef(null);
  const yearButtonRef = useRef(null);

  const openProjectDetails = (project) => {
    try {
      if (project?.id) {
        window.localStorage.setItem('ProjectManager_ProjectDisplay_id', String(project.id));
        dispatch(updateOrderDetails({}));
        dispatch(deleteAllTexts());
        history.push('/ProjectManager/DetailsProject');
      }
    } catch (e) { }
  };

  const openTextDetails = async (content) => {
    try {
      let d = null;
      try {
        const res = await axiosPrivate.get(`/texts/external/${content?.id}/fullDetails`);
        d = res?.data || null;
      } catch (e) { }

      dispatch(
        setCurrentRedaction({
          id: (d && d.id) || content?.id,
          title: (d && (d.textTitle || d.keyword)) || content?.keyword || '',
          textTitle: (d && (d.textTitle || d.keyword)) || content?.keyword || '',
          keyword: (d && d.keyword) || content?.keyword || '',
          url: (d && d.url) || '',
          titleTag: (d && d.titleTag) || '',
          metaDescriptionTag: (d && d.metaDescriptionTag) || '',
          initialHtmlContent: (d && d.initialHtmlContent) || '',
          initialTextContent: (d && d.initialTextContent) || '',
          actualHtmlContent: (d && d.actualHtmlContent) || '',
          actualTextContent: (d && d.actualTextContent) || '',
          semanticGap: (d && d.semanticGap) || [],
          analyseResult: (d && d.analyseResult) || null,
          stuffedKeywords: (d && d.stuffedKeywords) || [],
          internalLinking: (d && d.internalLinking) || '',
          delivered: !!(d && d.delivered),
          revisionSent: !!(d && d.revisionSent),
          revisionDone: !!(d && d.revisionDone),
          globalRevisionSent: !!(d && d.globalRevisionSent),
          validated: !!(d && d.validated),
          orderId: (d && d.projectId) || null,
          createdAt: (d && d.createdAt) || content?.createdAt || new Date().toISOString(),
          initialScoreData: (d && d.initialScoreData) || null,
        })
      );

      history.push('/ProjectManager/TextDetails');
    } catch (e) { }
  };

  const { clients: clientsFromStore } = useSelector(adminClientClientListSelector) || {};
  const clientsForChildren =
    Array.isArray(clientsFromStore)
      ? clientsFromStore
      : clientsFromStore === null
        ? STABLE_EMPTY_WEBSITES
        : [];

  useEffect(() => {
    try {
      const savedUrl = window.localStorage.getItem('AC_Dashboard_SelectedWebsite');
      if (savedUrl) setUrlSearch(savedUrl);
    } catch { }
    dispatch(fetchAdminClientClientList());
  }, [dispatch]);

  useEffect(() => {
    document.title = t('Typix - Mon tableau de bord');
  }, [t]);

  useEffect(() => {
    const fetchProjectStats = async () => {
      if (!urlSearch) {
        setProjectStats({
          numberOfProjectInProgress: 0,
          numberOfProjectInDelay: 0,
          numberOfProjectValidated: 0,
          numberOfProjects: 0,
        });
        return;
      }

      const rawList = clientsForChildren;
      const selectedWebsite = rawList.find(
        (c) => c?.urlSite === urlSearch || c?.url === urlSearch || c?.name === urlSearch
      );
      if (!selectedWebsite?.id) {
        setProjectStats({
          numberOfProjectInProgress: 0,
          numberOfProjectInDelay: 0,
          numberOfProjectValidated: 0,
          numberOfProjects: 0,
        });
        return;
      }

      setIsLoadingStats(true);
      try {
        const response = await axiosPrivate.get(`/project/stats/by-website/${selectedWebsite.id}`);
        const stats = response?.data || {};
        setProjectStats({
          numberOfProjectInProgress: stats.numberOfProjectInProgress || 0,
          numberOfProjectInDelay: stats.numberOfProjectInDelay || 0,
          numberOfProjectValidated: stats.numberOfProjectValidated || 0,
          numberOfProjects: stats.numberOfProjects || 0,
        });
      } catch (e) {
        setProjectStats({
          numberOfProjectInProgress: 0,
          numberOfProjectInDelay: 0,
          numberOfProjectValidated: 0,
          numberOfProjects: 0,
        });
      } finally {
        setIsLoadingStats(false);
      }
    };

    fetchProjectStats();
  }, [urlSearch, clientsForChildren]);

  useEffect(() => {
    const fetchLatestContents = async () => {
      if (!urlSearch) {
        setLatestContents([]);
        return;
      }

      const rawList = clientsForChildren;
      const selectedWebsite = rawList.find(
        (c) => c?.urlSite === urlSearch || c?.url === urlSearch || c?.name === urlSearch
      );

      if (!selectedWebsite?.id) {
        setLatestContents([]);
        return;
      }

      setIsLoadingContents(true);
      try {
        const response = await axiosPrivate.get(
          `/texts/external/by-website/${selectedWebsite.id}`
        );
        const contents = response?.data || [];
        const sortedContents = Array.isArray(contents)
          ? contents.sort((a, b) => new Date(b.createdAt || b.dateCreation) - new Date(a.createdAt || a.dateCreation))
          : [];
        setLatestContents(sortedContents);
      } catch (e) {
        setLatestContents([]);
      } finally {
        setIsLoadingContents(false);
      }
    };

    fetchLatestContents();
  }, [urlSearch, clientsForChildren]);

  useEffect(() => {
    const fetchLatestProjects = async () => {
      if (!urlSearch) {
        setLatestProjects([]);
        return;
      }

      const rawList = clientsForChildren;
      const selectedWebsite = rawList.find(
        (c) => c?.urlSite === urlSearch || c?.url === urlSearch || c?.name === urlSearch
      );

      if (!selectedWebsite?.id) {
        setLatestProjects([]);
        return;
      }

      setIsLoadingProjects(true);
      try {
        const response = await axiosPrivate.get(
          `/project/by-website/${selectedWebsite.id}`
        );
        const projects = response?.data || [];
        const sortedProjects = Array.isArray(projects)
          ? projects.sort((a, b) => new Date(b.createdAt || b.dateCreation || b.creationDate) - new Date(a.createdAt || a.dateCreation || a.creationDate))
          : [];
        setLatestProjects(sortedProjects);
      } catch (e) {
        setLatestProjects([]);
      } finally {
        setIsLoadingProjects(false);
      }
    };

    fetchLatestProjects();
  }, [urlSearch, clientsForChildren]);

  useEffect(() => {
    const fetchContentStats = async () => {
      if (!urlSearch) {
        setContentStats({
          numberOfTextInDraft: 0,
          numberOfTextDelivered: 0,
          numberOfTextDeployed: 0,
          numberOfTextValidated: 0,
          numberOfTextValidatedAndNotDeployed: 0,
          averageNumberOfTextDeployedPerMonth: 0,
          averageNumberOfTextDeployedPerYear: 0,
          averageNumberOfTextDeliveredPerMonth: 0,
          averageNumberOfTextDeliveredPerYear: 0,
          averageNumberOfProjectWhereAllTextDeliveredPerYear: 0,
          totalNumberOfText: 0,
        });
        return;
      }

      const rawList = clientsForChildren;
      const selectedWebsite = rawList.find(
        (c) => c?.urlSite === urlSearch || c?.url === urlSearch || c?.name === urlSearch
      );

      if (!selectedWebsite?.id) {
        setContentStats({
          numberOfTextInDraft: 0,
          numberOfTextDelivered: 0,
          numberOfTextDeployed: 0,
          numberOfTextValidated: 0,
          numberOfTextValidatedAndNotDeployed: 0,
          averageNumberOfTextDeployedPerMonth: 0,
          averageNumberOfTextDeployedPerYear: 0,
          averageNumberOfTextDeliveredPerMonth: 0,
          averageNumberOfTextDeliveredPerYear: 0,
          averageNumberOfProjectWhereAllTextDeliveredPerYear: 0,
          totalNumberOfText: 0,
        });
        return;
      }

      setIsLoadingContentStats(true);
      try {
        const response = await axiosPrivate.get(
          `/texts/external/stats/by-website/${selectedWebsite.id}`
        );
        const stats = response?.data || {};
        setContentStats({
          numberOfTextInDraft: stats.numberOfTextInDraft || 0,
          numberOfTextDelivered: stats.numberOfTextDelivered || 0,
          numberOfTextDeployed: stats.numberOfTextDeployed || 0,
          numberOfTextValidated: stats.numberOfTextValidated || 0,
          numberOfTextValidatedAndNotDeployed: stats.numberOfTextValidatedAndNotDeployed || 0,
          averageNumberOfTextDeployedPerMonth: stats.averageNumberOfTextDeployedPerMonth || 0,
          averageNumberOfTextDeployedPerYear: stats.averageNumberOfTextDeployedPerYear || 0,
          averageNumberOfTextDeliveredPerMonth: stats.averageNumberOfTextDeliveredPerMonth || 0,
          averageNumberOfTextDeliveredPerYear: stats.averageNumberOfTextDeliveredPerYear || 0,
          averageNumberOfProjectWhereAllTextDeliveredPerYear: stats.averageNumberOfProjectWhereAllTextDeliveredPerYear || 0,
          totalNumberOfText: stats.totalNumberOfText || 0,
        });
      } catch (e) {
        setContentStats({
          numberOfTextInDraft: 0,
          numberOfTextDelivered: 0,
          numberOfTextDeployed: 0,
          numberOfTextValidated: 0,
          numberOfTextValidatedAndNotDeployed: 0,
          averageNumberOfTextDeployedPerMonth: 0,
          averageNumberOfTextDeployedPerYear: 0,
          averageNumberOfTextDeliveredPerMonth: 0,
          averageNumberOfTextDeliveredPerYear: 0,
          averageNumberOfProjectWhereAllTextDeliveredPerYear: 0,
          totalNumberOfText: 0,
        });
      } finally {
        setIsLoadingContentStats(false);
      }
    };

    fetchContentStats();
  }, [urlSearch, clientsForChildren]);

  useEffect(() => {
    const fetchMonthly = async () => {
      if (!urlSearch) {
        setMonthlyTextsStats({ delivered: {}, deployed: {} });
        return;
      }

      const rawList = clientsForChildren;
      const selectedWebsite = rawList.find(
        (c) => c?.urlSite === urlSearch || c?.url === urlSearch || c?.name === urlSearch
      );

      if (!selectedWebsite?.id) {
        setMonthlyTextsStats({ delivered: {}, deployed: {} });
        return;
      }

      setIsLoadingMonthlyStats(true);
      try {
        const response = await axiosPrivate.get(
          `/texts/external/stats/by-website/per-month/${selectedWebsite.id}`
        );
        const data = response?.data || {};
        setMonthlyTextsStats({
          delivered: data?.delivered || {},
          deployed: data?.deployed || {},
        });
      } catch (e) {
        setMonthlyTextsStats({ delivered: {}, deployed: {} });
      } finally {
        setIsLoadingMonthlyStats(false);
      }
    };

    fetchMonthly();
  }, [urlSearch, clientsForChildren]);

  const availableYears = useMemo(() => {
    const yearsSet = new Set();

    if (monthlyTextsStats?.deployed && typeof monthlyTextsStats.deployed === 'object') {
      Object.keys(monthlyTextsStats.deployed).forEach(year => {
        if (year && !isNaN(Number(year))) {
          yearsSet.add(Number(year));
        }
      });
    }

    const years = Array.from(yearsSet).sort((a, b) => b - a);

    return years.length > 0 ? years : [new Date().getFullYear()];
  }, [monthlyTextsStats]);

  useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        yearDropdownRef.current &&
        yearButtonRef.current &&
        !yearButtonRef.current.contains(event.target) &&
        !yearDropdownRef.current.contains(event.target)
      ) {
        yearDropdownRef.current.classList.remove('year-dropdown-open');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    document.body.style.backgroundColor = '#F6F6F6';
    const appShell = document.querySelector('.app-shell');
    const wrapperContent = document.querySelector('.wrapper-content');
    if (appShell) appShell.style.backgroundColor = '#F6F6F6';
    if (wrapperContent) wrapperContent.style.backgroundColor = '#F6F6F6';

    return () => {

      document.body.style.backgroundColor = '';
      if (appShell) appShell.style.backgroundColor = '';
      if (wrapperContent) wrapperContent.style.backgroundColor = '';
    };
  }, []);

  return (
    <div className={`${styles['content-center']} ${styles['writer-home-page']}`}>
      <div className={styles['pro-header']}>
        <div className={styles['pro-header__inner']}>
          {/* Col gauche : breadcrumb */}
          <div className={styles['pro-header__breadcrumb']}>
            <span className={`${styles['pro-header__breadcrumb-item']} ${styles['pro-header__breadcrumb-item--main']}`}>{t('Tableau de bord')}</span>
            {urlSearch && (
              <>
                <span className={styles['pro-header__breadcrumb-separator']}>›</span>
                <span className={`${styles['pro-header__breadcrumb-item']} ${styles['pro-header__breadcrumb-item--site']}`}>
                  {(() => {
                    const toDomain = (u) =>
                      (u || '').replace(/^https?:\/\//i, '').replace(/\/.*$/, '').trim();
                    return toDomain(urlSearch);
                  })()}
                </span>
              </>
            )}
          </div>

          {/* Col droite : actions */}
          <div className={styles['pro-header__actions']}>

            <div className={styles['pro-header__picker']}>
              <WebsiteFilter
                websitesList={clientsForChildren}
                selectedWebsiteUrl={urlSearch}
                setSelectedWebsiteUrl={setUrlSearch}
                storageKey="AC_Dashboard_SelectedWebsite"
                style={{ minWidth: 280, maxWidth: 500 }}
              />
            </div>
          </div>

        </div>
      </div>
      <hr className={styles['pro-header__divider']} />
      <div className={styles['main-content-layout']}>
        <div className={styles['left-column']}>
          <div className="cards-row">
            <div className="Performances">
              <h2 className={styles['section-title']} style={{ marginBottom: '20px', textAlign: 'left' }}>
                {t('% projets par statut de validation :')}
              </h2>

              {(() => {
                const totalProjects = projectStats.numberOfProjects || 0;
                const inProgressPct =
                  totalProjects > 0
                    ? Math.round((projectStats.numberOfProjectInProgress / totalProjects) * 100)
                    : 0;
                const delayedPct =
                  totalProjects > 0
                    ? Math.round((projectStats.numberOfProjectInDelay / totalProjects) * 100)
                    : 0;
                const validatedPct =
                  totalProjects > 0
                    ? Math.round((projectStats.numberOfProjectValidated / totalProjects) * 100)
                    : 0;

                const totalPct = Math.min(
                  inProgressPct + delayedPct + validatedPct,
                  100
                );
                const radius = 50;
                const circumference = 2 * Math.PI * radius;

                const safePct = (v) => (Number.isFinite(v) && v > 0 ? Math.min(v, 100) : 0);
                const pPct = safePct(inProgressPct);
                const dPct = safePct(delayedPct);
                const vPct = safePct(validatedPct);

                const pLen = (pPct / 100) * circumference;
                const dLen = (dPct / 100) * circumference;
                const vLen = (vPct / 100) * circumference;

                const segments = [];
                if (pLen > 0) segments.push({ color: '#FF855D', length: pLen, label: t('En cours') });
                if (dLen > 0) segments.push({ color: '#C06DD8', length: dLen, label: t('En retard') });
                if (vLen > 0) segments.push({ color: '#63D3D7', length: vLen, label: t('Validé') });

                const segmentsWithOffsets = segments.reduce((acc, seg) => {
                  const offset = acc.currentOffset;
                  acc.segments.push({
                    ...seg,
                    offset,
                    dashArray: `${seg.length} ${circumference - seg.length}`,
                  });
                  acc.currentOffset += seg.length;
                  return acc;
                }, { currentOffset: 0, segments: [] }).segments;

                const reversedSegments = [...segmentsWithOffsets].reverse();

                return (
                  <div className="donut-row">
                    <div className="donut-chart-container">
                      <div className="donut-chart">
                        <svg
                          className="donut-svg"
                          viewBox="0 0 120 120"
                        >
                          <circle
                            className="donut-bg"
                            cx="60"
                            cy="60"
                            r={radius}
                            fill="transparent"
                            stroke="#F3F4F6"
                            strokeWidth="12"
                          />
                          {reversedSegments.map((seg, reversedIndex) => (
                            <circle
                              key={`circle-${reversedIndex}`}
                              cx="60"
                              cy="60"
                              r={radius}
                              fill="transparent"
                              stroke={seg.color}
                              strokeWidth="12"
                              strokeLinecap="round"
                              strokeDasharray={seg.dashArray}
                              strokeDashoffset={-seg.offset}
                            />
                          ))}
                          <circle
                            cx="60"
                            cy="60"
                            r="40"
                            fill="white"
                            fillOpacity="0"
                            style={{ pointerEvents: 'all', cursor: 'default' }}
                          />
                          <text
                            x="60"
                            y="60"
                            textAnchor="middle"
                            dominantBaseline="middle"
                            style={{
                              fontSize: '11px',
                              fontWeight: 600,
                              fill: '#111827',
                              pointerEvents: 'none',
                            }}
                          >
                            {`${totalProjects || 0} ${totalProjects > 1 ? t('projets') : t('projet')
                              }`}
                          </text>
                        </svg>
                      </div>
                    </div>

                    <div className="legend-container">
                      <div className="legend-item">
                        <div className="legend-dot blue-dot"></div>
                        <span className="legend-text">
                          <span style={{ color: '#FF855D', fontWeight: 600, marginRight: 4 }}>
                            {projectStats.numberOfProjectInProgress || 0}
                          </span>
                          {t('En cours')}
                        </span>
                      </div>

                      <div className="legend-item">
                        <div className="legend-dot purple-dot"></div>
                        <span className="legend-text">
                          <span style={{ color: '#C06DD8', fontWeight: 600, marginRight: 4 }}>
                            {projectStats.numberOfProjectInDelay || 0}
                          </span>
                          {t('En retard')}
                        </span>
                      </div>

                      <div className="legend-item">
                        <div className="legend-dot green-dot"></div>
                        <span className="legend-text">
                          <span style={{ color: '#63D3D7', fontWeight: 600, marginRight: 4 }}>
                            {projectStats.numberOfProjectValidated || 0}
                          </span>
                          {t('Validé')}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="Performances second-card">
              <h2 className={styles['section-title']} style={{ marginBottom: '8px', textAlign: 'left' }}>
                {t("Statut d'avancement des contenus:")}
              </h2>

              <div className="bar-chart-container">
                <div className="bars-wrapper">
                  <div className="y-axis-label">
                    <span className="y-axis-text">{t('Pourcentage')}</span>
                  </div>

                  <div className="bars-container">
                    <span className="y-axis-max">100</span>
                    <span className="y-axis-min">0</span>

                    <div className="bar-grid">
                      <div className="bar-grid-line" style={{ top: '5%' }} />
                      <div className="bar-grid-line" style={{ top: '25%' }} />
                      <div className="bar-grid-line" style={{ top: '50%' }} />
                      <div className="bar-grid-line" style={{ top: '75%' }} />
                      <div className="bar-grid-line" style={{ top: '100%' }} />
                    </div>

                    <div className="bar-item">
                      {(() => {
                        const count = contentStats.numberOfTextInDraft || 0;
                        const pct = urlSearch && contentStats.totalNumberOfText > 0
                          ? Math.min((count / contentStats.totalNumberOfText) * 100, 100)
                          : 0;
                        const isTiny = pct < 12 && pct > 0;
                        const hasValue = count > 0 && pct > 0;
                        const isZero = count === 0;
                        return (
                          <div
                            className={`bar red-bar ${isTiny ? 'bar--tiny' : ''} ${!hasValue && !isZero ? 'bar--empty' : ''}`}
                            style={{
                              height: hasValue ? `${pct}%` : '4px',
                              background: isZero ? undefined : (hasValue ? undefined : 'transparent'),
                              boxShadow: isZero ? undefined : (hasValue ? undefined : 'none'),
                            }}
                          >
                            {hasValue && <div className="bar-percentage">{count}</div>}
                          </div>
                        );
                      })()}
                    </div>

                    <div className="bar-item">
                      {(() => {
                        const count = contentStats.numberOfTextDelivered || 0;
                        const pct = urlSearch && contentStats.totalNumberOfText > 0
                          ? Math.min((count / contentStats.totalNumberOfText) * 100, 100)
                          : 0;
                        const isTiny = pct < 12 && pct > 0;
                        const hasValue = count > 0 && pct > 0;
                        const isZero = count === 0;
                        return (
                          <div
                            className={`bar green-bar ${isTiny ? 'bar--tiny' : ''} ${!hasValue && !isZero ? 'bar--empty' : ''}`}
                            style={{
                              height: hasValue ? `${pct}%` : '4px',
                              background: isZero ? undefined : (hasValue ? undefined : 'transparent'),
                              boxShadow: isZero ? undefined : (hasValue ? undefined : 'none'),
                            }}
                          >
                            {hasValue && <div className="bar-percentage">{count}</div>}
                          </div>
                        );
                      })()}
                    </div>

                    <div className="bar-item">
                      {(() => {
                        const count = contentStats.numberOfTextValidated || 0;
                        const pct = urlSearch && contentStats.totalNumberOfText > 0
                          ? Math.min((count / contentStats.totalNumberOfText) * 100, 100)
                          : 0;
                        const isTiny = pct < 12 && pct > 0;
                        const hasValue = count > 0 && pct > 0;
                        const isZero = count === 0;
                        return (
                          <div
                            className={`bar blue-bar ${isTiny ? 'bar--tiny' : ''} ${!hasValue && !isZero ? 'bar--empty' : ''}`}
                            style={{
                              height: hasValue ? `${pct}%` : '4px',
                              background: isZero ? undefined : (hasValue ? undefined : 'transparent'),
                              boxShadow: isZero ? undefined : (hasValue ? undefined : 'none'),
                            }}
                          >
                            {hasValue && <div className="bar-percentage">{count}</div>}
                          </div>
                        );
                      })()}
                    </div>

                    <div className="bar-item">
                      {(() => {
                        const count = contentStats.numberOfTextDeployed || 0;
                        const pct = urlSearch && contentStats.totalNumberOfText > 0
                          ? Math.min((count / contentStats.totalNumberOfText) * 100, 100)
                          : 0;
                        const isTiny = pct < 12 && pct > 0;
                        const hasValue = count > 0 && pct > 0;
                        const isZero = count === 0;
                        return (
                          <div
                            className={`bar cyan-bar ${isTiny ? 'bar--tiny' : ''} ${!hasValue && !isZero ? 'bar--empty' : ''}`}
                            style={{
                              height: hasValue ? `${pct}%` : '4px',
                              background: isZero ? undefined : (hasValue ? undefined : 'transparent'),
                              boxShadow: isZero ? undefined : (hasValue ? undefined : 'none'),
                            }}
                          >
                            {hasValue && <div className="bar-percentage">{count}</div>}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              <div className="status-legend">
                <div className="legend-item">
                  <span className="legend-dot yellow-dot"></span>
                  <span className="legend-text">{t('Brouillon')}</span>
                </div>
                <div className="legend-item">
                  <span className="legend-dot blue-dot"></span>
                  <span className="legend-text">{t('Livré')}</span>
                </div>
                <div className="legend-item">
                  <span className="legend-dot purple-dot"></span>
                  <span className="legend-text">{t('Validé')}</span>
                </div>
                <div className="legend-item">
                  <span className="legend-dot green-dot"></span>
                  <span className="legend-text">{t('Publié')}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="full-width-card" id="monthly-stat">
            <div className="card-header">
              <h2 className={styles['section-title']}>
                {t('Nombre de textes publiés ')}
              </h2>
              <div className="year-filter-container">
                <button
                  ref={yearButtonRef}
                  className="year-filter-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (yearDropdownRef.current) {
                      yearDropdownRef.current.classList.toggle('year-dropdown-open');
                    }
                  }}
                >
                  <img
                    src="/Images/icon-date.png"
                    alt="date icon"
                    className="year-filter-icon"
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span className="year-filter-text">{selectedYear}</span>
                  <svg
                    className="year-filter-arrow"
                    width="12"
                    height="8"
                    viewBox="0 0 12 8"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M1 1.5L6 6.5L11 1.5" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <div ref={yearDropdownRef} className="year-dropdown">
                  {availableYears.length > 0 ? (
                    availableYears.map((year) => (
                      <button
                        key={year}
                        className={`year-dropdown-item ${selectedYear === year ? 'year-dropdown-item--active' : ''}`}
                        onClick={() => {
                          setSelectedYear(year);
                          if (yearDropdownRef.current) {
                            yearDropdownRef.current.classList.remove('year-dropdown-open');
                          }
                        }}
                      >
                        {year}
                      </button>
                    ))
                  ) : (
                    <button
                      className="year-dropdown-item"
                      disabled
                    >
                      {t('Aucune donnée')}
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="card-content">
              <div className="monthly-chart-container">
                <div className="monthly-bars-wrapper">
                  <div className="monthly-bars-container">

                    <div className="bar-grid">
                      <div className="bar-grid-line" style={{ top: '15%' }} />
                      <div className="bar-grid-line" style={{ top: '35%' }} />
                      <div className="bar-grid-line" style={{ top: '55%' }} />
                      <div className="bar-grid-line" style={{ top: '75%' }} />
                      <div className="bar-grid-line" style={{ top: '93%' }} />
                    </div>
                    {(() => {
                      const monthColors = {
                        january: 'rgba(224, 228, 255, 0.75)',
                        february: 'rgba(199, 205, 254, 0.75)',
                        march: 'rgba(199, 205, 254, 0.75)',
                        april: 'rgba(224, 228, 255, 0.75)',
                        may: 'rgba(238, 240, 255, 0.75)',
                        june: 'rgba(238, 240, 255, 0.75)',
                        july: 'rgba(224, 228, 255, 0.75)',
                        august: 'rgba(100, 88, 241, 0.75)',
                        september: 'rgba(224, 228, 255, 0.75)',
                        october: 'rgba(224, 228, 255, 0.75)',
                        november: 'rgba(224, 228, 255, 0.75)',
                        december: 'rgba(199, 205, 254, 0.75)',
                      };

                      const createGradient = (rgbaColor) => {
                        // Extraire les valeurs RGB de la chaîne rgba(r, g, b, a)
                        const match = rgbaColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
                        if (match) {
                          const r = match[1];
                          const g = match[2];
                          const b = match[3];
                          return `linear-gradient(to top, rgba(${r}, ${g}, ${b}, 0.65) 0%, rgba(${r}, ${g}, ${b}, 1) 45%)`;
                        }
                        return rgbaColor; // Fallback si le parsing échoue
                      };

                      const months = [
                        { key: 'january', label: t('Jan') },
                        { key: 'february', label: t('Fév') },
                        { key: 'march', label: t('Mars') },
                        { key: 'april', label: t('Avr') },
                        { key: 'may', label: t('Mai') },
                        { key: 'june', label: t('Juin') },
                        { key: 'july', label: t('Juil') },
                        { key: 'august', label: t('Août') },
                        { key: 'september', label: t('Sept') },
                        { key: 'october', label: t('Oct') },
                        { key: 'november', label: t('Nov') },
                        { key: 'december', label: t('Déc') },
                      ];

                      // Filtrer les données par année sélectionnée
                      const deployedForYear = monthlyTextsStats?.deployed?.[selectedYear] || {};
                      const max = Math.max(1, ...months.map(m => Number(deployedForYear[m.key] || 0)));

                      return months.map((m) => {
                        const value = Number(deployedForYear[m.key] || 0);
                        const height = Math.round((value / max) * 120);
                        return (
                          <div key={m.key} className="month-bar-item">
                            <div className="month-value">{value}</div>
                            <div className="month-bar" style={{ height: `${height}px`, background: createGradient(monthColors[m.key]) }}></div>
                            <div className="month-label">{m.label}</div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles['right-column']}>
          <div className={styles['content-section']} id="latest-contents-section" style={{ marginTop: '-5px' }}>
            <div className={styles['section-header'] + ' latest-contents-header'}>
              <h2 className={styles['section-title']}>{t('Derniers contenus ajoutés')}</h2>
            </div>

            <div className="performance-table-wrapper" style={{ marginTop: '15px' }}>
              <table className="performance-table">
                <thead>
                  <tr>
                    <th className="col-keyword"> {t('Texte')}</th>
                    <th> {t('Statut')}</th>
                    <th> {t('Ajouté')}</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingContents ? (
                    <tr style={{ background: 'transparent' }}>
                      <td
                        className="keyword-cell"
                        colSpan={3}
                        style={{
                          textAlign: 'center',
                          color: '#666',
                          background: 'transparent',
                          border: 'none'
                        }}
                      >
                        {t('Chargement...')}
                      </td>
                    </tr>
                  ) : latestContents.length > 0 ? (
                    <>
                      {latestContents.slice(0, 5).map((content, idx) => {
                        return (
                          <tr
                            key={content.id || idx}
                            onClick={() => openTextDetails(content)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td className="keyword-cell">
                              {content.keyword || 'Sans titre'}
                            </td>
                            <td>
                              <div
                                style={{
                                  textAlign: 'center',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '6px',
                                  position: 'relative',
                                }}
                              >
                                <img
                                  src={getTextStatusIcon(content)}
                                  alt={getTextStatusLabel(content)}
                                  style={{
                                    width: '16px',
                                    height: '16px',
                                  }}
                                />
                                <span style={{ fontSize: '12px', fontWeight: 500 }}>{getTextStatusLabel(content)}</span>
                              </div>
                            </td>
                            <td>
                              {content.createdAt
                                ? formatDateShort(content.createdAt)
                                : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </>
                  ) : (
                    <tr style={{ background: 'transparent' }}>
                      <td
                        className="keyword-cell"
                        colSpan={3}
                        style={{
                          textAlign: 'center',
                          color: '#666',
                          background: 'transparent',
                          border: 'none'
                        }}
                      >
                        {urlSearch ? t('Aucun contenu disponible pour ce site web.') : t('Sélectionnez un site web pour voir les contenus.')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className={styles['content-section']} id="latest-project-section">
            <div className={styles['section-header'] + ' latest-contents-header'}>
              <h2 className={styles['section-title']}>{t('Derniers projets créés')}</h2>
            </div>

            <div className="performance-table-wrapper" style={{ marginTop: '15px' }}>
              <table className="performance-table">
                <thead>
                  <tr>
                    <th className="col-keyword" style={{ maxWidth: 180, width: 180 }}>{t('Titre du projet')}</th>
                    <th style={{ textAlign: 'left' }}>{t('Statut')}</th>
                    <th>{t('Crée')}</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingProjects ? (
                    <tr style={{ background: 'transparent' }}>
                      <td
                        className="keyword-cell"
                        colSpan={3}
                        style={{
                          textAlign: 'center',
                          color: '#666',
                          background: 'transparent',
                          border: 'none'
                        }}
                      >
                        {t('Chargement...')}
                      </td>
                    </tr>
                  ) : latestProjects.length > 0 ? (
                    latestProjects.slice(0, 5).map((project, idx) => (
                      <tr
                        key={project.id || idx}
                        onClick={() => openProjectDetails(project)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className="keyword-cell" style={{ maxWidth: 180, width: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {project.title ||
                            project.titre ||
                            project.name ||
                            project.projectName ||
                            'Sans titre'}
                        </td>
                        <td>
                          {(() => {
                            const status = project.status;
                            if (!status) return '-';
                            return (
                              <div
                                style={{
                                  textAlign: 'left',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'flex-start',
                                  gap: '6px',
                                  position: 'relative',
                                }}
                              >
                                <img
                                  src={getProjectStatusIcon(project)}
                                  alt={getProjectStatusLabel(project)}
                                  style={{
                                    width: '16px',
                                    height: '16px',
                                  }}
                                />
                                <span style={{ fontSize: '12px', fontWeight: 500 }}>{getProjectStatusLabel(project)}</span>
                              </div>
                            );
                          })()}
                        </td>
                        <td>
                          {project.createdAt ||
                            project.dateCreation ||
                            project.creationDate
                            ? formatDateShort(
                              project.createdAt ||
                              project.dateCreation ||
                              project.creationDate
                            )
                            : '-'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr style={{ background: 'transparent' }}>
                      <td
                        className="keyword-cell"
                        colSpan={3}
                        style={{
                          textAlign: 'center',
                          color: '#666',
                          background: 'transparent',
                          border: 'none'
                        }}
                      >
                        {urlSearch
                          ? t('Aucun projet créé pour le moment.')
                          : t('Sélectionnez un site web pour voir les projets.')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


